// validator.ts
import { validateSymbol } from "./decorators.js";
import { MultipleValidationError } from "./errors/MultipleValidationError.js";
import { ValidationError } from "./errors/ValidationError.js";
var validateFields = Symbol("fieldsToValidate");
var Validator = class _Validator {
  constructor(functions, recurse = false) {
    this.functions = functions;
    this.recurse = recurse;
  }
  /**
   * constructs a new Validator from a decorated class or an instance of an a decorated class.
   */
  static fromDecoratedClass(target) {
    const validator = target.validator ?? target.prototype.validator;
    if (!validator)
      throw new Error("could not find validator on class. Checked target.validator and target.prototype.validator.");
    const functions = { ...validator.functions };
    delete functions[validateSymbol];
    return new _Validator(functions, validator.recurse);
  }
  /**
   * validates the target object.
   */
  async validate(target, {
    aggregateErrors = false,
    recurse = this.recurse,
    source = target,
    path = [],
    visited = /* @__PURE__ */ new Set(),
    ...additionalMeta
  } = {}) {
    const options = { aggregateErrors, recurse, source, path, visited, ...additionalMeta };
    const ownFields = Object.keys(this.functions);
    const targetFields = target[validateFields]?.(options);
    const fieldsToValidate = Array.isArray(targetFields) ? Array.from(new Set(ownFields.concat(targetFields))) : ownFields;
    return this.validateKeys(target, fieldsToValidate, options);
  }
  /**
   * Validate a specific key of the target object. Does **not** respect the `validatedFields` symbol function on the target.
   */
  async validateKey(target, key, {
    aggregateErrors = false,
    recurse = this.recurse,
    source = target,
    path = [],
    visited = /* @__PURE__ */ new Set(),
    ...additionalMeta
  } = {}) {
    const func = this.getFunction(target, key);
    const toCheck = this.readValue(target, key);
    if (func) {
      try {
        const stopRecursing = await func({
          value: toCheck,
          key,
          target,
          aggregateErrors,
          recurse,
          source,
          path,
          ...additionalMeta
        });
        if (stopRecursing)
          return;
      } catch (error) {
        if (error instanceof ValidationError) {
          if (error.extensions.key === ValidationError.DEFAULT_KEY)
            error.extensions.key = key;
          error.extensions.path = error.extensions.path?.length ? path.concat(error.extensions.path) : path;
          error.target = target;
          error.source = source;
        }
        throw error;
      }
    }
    if (recurse && toCheck !== null && typeof toCheck === "object") {
      return this._validateRecursive(toCheck, {
        aggregateErrors,
        recurse,
        source,
        path: [...path, key],
        visited,
        ...additionalMeta
      });
    }
  }
  /**
   * Validate a specific set of keys on the target object
   */
  async validateKeys(target, keys, {
    aggregateErrors = false,
    recurse = this.recurse,
    source = target,
    path = [],
    visited = /* @__PURE__ */ new Set(),
    ...additionalMeta
  } = {}) {
    const errors = [];
    for (const key of keys) {
      try {
        await this.validateKey(target, key, {
          aggregateErrors,
          recurse,
          source,
          path: [...path],
          visited,
          ...additionalMeta
        });
      } catch (error) {
        if (!aggregateErrors)
          throw error;
        if (!(error instanceof ValidationError || error instanceof MultipleValidationError))
          throw error;
        errors.push(error);
      }
    }
    if (errors.length === 1)
      throw errors[0];
    if (errors.length)
      throw new MultipleValidationError(errors);
  }
  /**
   * Validate a specific value inside the target object determined by a dot-separated path.
   * You can index arrays by using the index of the element you want to check: `array.0.key`
   */
  async validatePath(target, path, {
    aggregateErrors = false,
    recurse = this.recurse,
    source = target,
    visited = /* @__PURE__ */ new Set(),
    ...additionalMeta
  } = {}) {
    if (!Array.isArray(path))
      path = path.split(".");
    let entry = target;
    let index = -1;
    while (++index < path.length - 1) {
      const currentKey = path[index];
      entry = this.readValue(entry, currentKey);
      if (entry === null || typeof entry !== "object")
        return console.warn("validatePath: encountered primitive but theres still path left to go.", path.slice(index));
      if (!Array.isArray(entry))
        visited.add(entry);
    }
    return (entry?.validator).validateKey(entry, path[index], {
      aggregateErrors,
      recurse,
      source,
      visited,
      ...additionalMeta,
      path: path.slice(0, -1)
    });
  }
  /**
   * helper function to read a value from the target and catch any errors that occur while doing so.
   */
  readValue(target, key, getValueFunction = "getValue") {
    try {
      if (target.validator && target.validator !== this) {
        return target.validator.readValue(target, key);
      }
      return target[getValueFunction]?.(key) ?? target[key];
    } catch (error) {
      console.warn("cannot get", key, "from", target, "using", getValueFunction, "\n", error);
    }
  }
  /**
   * helper function to get the validation function from the prototype of the target if neccessary.
   *
   * The decorator writes the functions to the prototype, and if the object has an own validator it has to check there.
   */
  getFunction(target, key) {
    const ownFunc = this.functions[key];
    if (ownFunc)
      return ownFunc;
    const proto = Object.getPrototypeOf(target);
    return proto?.validator?.functions[key];
  }
  /**
   * helper function to step into (potentially nested) arrays.
   */
  // eslint-disable-next-line complexity
  async _validateRecursive(target, {
    aggregateErrors = false,
    source = target,
    path = [],
    visited = /* @__PURE__ */ new Set(),
    ...additionalMeta
  } = {}) {
    if (!Array.isArray(target)) {
      if (visited.has(target))
        return;
      visited.add(target);
      const options = {
        aggregateErrors,
        source,
        visited,
        ...additionalMeta,
        recurse: true,
        path: [...path]
      };
      if (typeof target?.validate === "function") {
        await target.validate(options);
      } else if (typeof target?.validator?.validate === "function") {
        await target.validator.validate(target, options);
      }
      return;
    }
    const errors = [];
    for (let index = 0; index < target.length; index++) {
      const item = target[index];
      try {
        await this._validateRecursive(item, {
          aggregateErrors,
          source,
          visited,
          ...additionalMeta,
          recurse: true,
          path: [...path, index + ""]
        });
      } catch (error) {
        if (!aggregateErrors)
          throw error;
        if (!(error instanceof ValidationError || error instanceof MultipleValidationError))
          throw error;
        errors.push(error);
      }
    }
    if (errors.length)
      throw new MultipleValidationError(errors);
  }
};
export {
  Validator,
  validateFields
};
